import { useQuery } from '@tanstack/react-query'
import { Link, useHistory } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { FaUniversity } from 'react-icons/fa'

import { PaymentRemindersAndCollectionForm } from './PaymentRemindersAndCollectionForm'
import { kraviaOnboardStatus } from '@root/query'
import { useBusinessContext, useFeature } from '@root/context'
import { Alert, AnimatedContentLoader, Button } from '@root/components'
import { getSettingsPageUrl } from '@constants'
import styled from 'styled-components'
import { useBankMonitoringStatus } from '@pages/InvoicingPage/useBankMonitoringStatus'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { PageBody, PageSubtitle } from './PaymentRemindersAndCollectionForm.styled'

const useDebtCollectionData = () => {
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const { slug, data: business } = useBusinessContext()
  const { isLoading: isBankMonitoringLoading, variant: bankMonitoringVariant } =
    useBankMonitoringStatus()

  const { data: kraviaData, isLoading: isKraviaLoading } = useQuery({
    queryKey: [slug, 'kravia_onboard_status'],
    queryFn: () => kraviaOnboardStatus({ slug }),
    enabled: kraviaEnabled
  })

  return {
    business,
    slug,
    kraviaData,
    bankMonitoringVariant,
    isLoading: isBankMonitoringLoading || (kraviaEnabled && isKraviaLoading)
  }
}

const BankConnectionRequiredView = ({ slug }: { slug: string }) => {
  const { t } = useTranslation()
  const history = useHistory()

  return (
    <SettingsSubPageLayout
      title={t('settings.sections.paymentRemindersAndCollection')}
      subTitle={<PageSubtitle>{t('settings.invoicing.paymentRemindersSubtitle')}</PageSubtitle>}
      contentPadding="none"
    >
      <PageBody>
        <Alert
          type="info"
          marginBottom={0}
          title={t('settings.invoicing.bankConnectionRequiredTitle')}
          description={
            <>
              {t('settings.invoicing.bankConnectionRequiredDescription')}
              <AlertActions>
                <Button
                  type="button"
                  intent="primary"
                  icon={<FaUniversity />}
                  onClick={() => history.push(getSettingsPageUrl(slug, { page: 'integrations' }))}
                >
                  {t('settings.invoicing.enableBankIntegration')}
                </Button>
              </AlertActions>
            </>
          }
        />
      </PageBody>
    </SettingsSubPageLayout>
  )
}

const BankSyncStaleAlert = ({ slug }: { slug: string }) => (
  <AlertWrap>
    <Alert
      type="warning"
      description={
        <Trans i18nKey="settings.invoicing.automaticDebtCollectionBankSyncStaleInfo">
          <Link to={getSettingsPageUrl(slug, { page: 'integrations' })} />
          <Link to={getSettingsPageUrl(slug, { page: 'holvi' })} />
        </Trans>
      }
    />
  </AlertWrap>
)

const InvoicingSettingAlert = ({ slug }: { slug: string }) => (
  <AlertWrap>
    <Alert
      type="info"
      description={
        <Trans i18nKey="settings.invoicing.automaticDebtCollectionAddInvoicingInfo">
          <Link to={getSettingsPageUrl(slug, { page: 'invoicing-general' })} />
        </Trans>
      }
    />
  </AlertWrap>
)

const InvoiceSettingsDebtCollection: React.FC = () => {
  const { business, slug, kraviaData, bankMonitoringVariant, isLoading } = useDebtCollectionData()

  const canInvoice = business?.can_invoice
  const isKraviaVerified = Boolean(kraviaData?.kravia_verified)

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      {bankMonitoringVariant === 'missing' ? (
        <BankConnectionRequiredView slug={slug} />
      ) : (
        <PaymentRemindersAndCollectionForm isKraviaVerified={isKraviaVerified}>
          {bankMonitoringVariant === 'stale' && <BankSyncStaleAlert slug={slug} />}
          {!canInvoice && <InvoicingSettingAlert slug={slug} />}
        </PaymentRemindersAndCollectionForm>
      )}
    </AnimatedContentLoader>
  )
}

export default InvoiceSettingsDebtCollection

const AlertWrap = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem 0;
`

const AlertActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`
